import { Vector3 } from "three";

export type GridType = "square" | "hex";

const SQRT3 = Math.sqrt(3);
const DEFAULT_GRID_SIZE = 1;

export interface HexCoordinate {
  q: number;
  r: number;
  s: number;
}

export function normalizeGridSize(rawGridSize?: number): number {
  if (
    typeof rawGridSize !== "number" ||
    !Number.isFinite(rawGridSize) ||
    rawGridSize <= 0
  ) {
    return DEFAULT_GRID_SIZE;
  }
  return rawGridSize;
}

export function normalizeGridType(rawGridType: unknown): GridType {
  return rawGridType === "hex" ? "hex" : "square";
}

export function snapToGrid(
  position: Vector3,
  gridSize: number,
  gridType: GridType,
): Vector3 {
  const size = normalizeGridSize(gridSize);

  if (gridType === "hex") {
    return snapToHexGrid(position, size);
  }

  return snapToSquareGrid(position, size);
}

function snapToSquareGrid(position: Vector3, gridSize: number): Vector3 {
  return new Vector3(
    Math.round(position.x / gridSize) * gridSize,
    position.y,
    Math.round(position.z / gridSize) * gridSize,
  );
}

export function worldToHex(position: Vector3, gridSize: number): HexCoordinate {
  const size = normalizeGridSize(gridSize);

  const q = (2 / 3) * (position.x / size);
  const r = ((-1 / 3) * position.x + (1 / SQRT3) * position.z) / size;
  const s = -q - r;

  return { q, r, s };
}

export function roundHexCoordinate(hex: HexCoordinate): HexCoordinate {
  let q = Math.round(hex.q);
  let r = Math.round(hex.r);
  let s = Math.round(hex.s);

  const qDiff = Math.abs(q - hex.q);
  const rDiff = Math.abs(r - hex.r);
  const sDiff = Math.abs(s - hex.s);

  if (qDiff > rDiff && qDiff > sDiff) {
    q = -r - s;
  } else if (rDiff > sDiff) {
    r = -q - s;
  } else {
    s = -q - r;
  }

  return { q, r, s };
}

export function hexToWorld(hex: HexCoordinate, gridSize: number): Vector3 {
  const size = normalizeGridSize(gridSize);
  return new Vector3(
    size * (1.5 * hex.q),
    0,
    size * (SQRT3 * (hex.r + hex.q / 2)),
  );
}

function snapToHexGrid(position: Vector3, gridSize: number): Vector3 {
  const hex = roundHexCoordinate(worldToHex(position, gridSize));
  return hexToWorld(hex, gridSize).setY(position.y);
}

export function hexCellCornerPoints(cellRadius: number): [number, number][] {
  const size = normalizeGridSize(cellRadius);
  return Array.from({ length: 6 }, (_, i) => {
    const angle = (Math.PI / 3) * i;
    return [size * Math.cos(angle), size * Math.sin(angle)];
  });
}

export function getSceneGridConfig(sceneProperties?: Record<string, unknown>) {
  const gridSize = normalizeGridSize(
    typeof sceneProperties?.gridSize === "number"
      ? sceneProperties.gridSize
      : undefined,
  );
  const gridType = normalizeGridType(sceneProperties?.gridType);
  const snapToGrid = sceneProperties?.snapToGrid === true;

  return {
    gridSize,
    gridType,
    snapToGrid,
  };
}
